/*
 * License Template area (audit Phase 3.3). Replaces
 * wwwroot/js/LicenseTemplate/List.js and Detail.js on the Phase 3.1 core.
 * No jQuery.
 *
 * A template is the shape a license is pressed from: header, Properties, a
 * Domain Binder written as JSON, and Quotas. Pressing one issues a real
 * License, which is why this file reaches into areas/license.ts for the last
 * step -- and why that step is the one thing here that changed shape.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { getTopView } from '../core/nav';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { url as absoluteUrl } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';
import { List as LicenseList, currentDomainId, failToast, summaryOf } from './license';

const MODAL = 'LicenseTemplateModal';
const CREATE_FORM = 'LicenseTemplateCreateForm';
const PRESS_FORM = 'LicenseTemplatePressForm';
const DETAIL_ALERT = 'LicenseTemplateDetailMessageContainer';

function templateId(): string {
    return (one('#LicenseTemplateId') as HTMLInputElement | null)?.value ?? '';
}

function busy(working: boolean): void {
    for (const node of all('#LicenseTemplateBusyIndicator')) node.classList.toggle('d-none', !working);
}

/** Swap a form's action icon for a spinner while its request is in flight. */
function working(formId: string, icon: string): (on: boolean) => void {
    return on => {
        for (const node of all(`#${formId} ${icon}`)) node.classList.toggle('d-none', on);
        for (const node of all(`#${formId} .fa-spinner`)) node.classList.toggle('d-none', !on);
    };
}

export const List = {
    ShowDetail(id: string): void {
        void ajaxModal({
            modal: MODAL,
            url: `LicenseTemplate/Detail/${id}`,
            title: null,
            errorTitle: `Template ${id}`,
            errorMessage: 'Template Detail failed to load:',
        });
    },

    NewTemplate(): void {
        void ajaxModal({
            modal: MODAL,
            url: `LicenseTemplate/Create/${currentDomainId()}`,
            formId: CREATE_FORM,
            focusId: 'Name',
            errorTitle: 'New License Template',
            errorMessage: 'Failed to render new template form',
        });
    },

    NewTemplateSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const spin = working(CREATE_FORM, '.fa-save');
        spin(true);
        void ajaxJson({
            url: 'LicenseTemplate',
            method: 'POST',
            data: serialize(form),
            alertInto: 'LicenseTemplateCreateMessageContainer',
            errorTitle: 'Failed to create template',
            onSuccess: data => {
                hideModal(MODAL);
                toast('success', 'Template created');
                const id = (data as { id?: string }).id;
                // Straight into the new template so Properties and the binder
                // shape can be filled in without hunting for the row.
                void List.Reload().then(() => { if (id) List.ShowDetail(id); });
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, '#LicenseTemplateCreateForm');
                toast('danger', 'Failed to create template', summaryOf(data));
                return true;
            },
            onComplete: () => spin(false),
        });
    },

    PressTemplate(id: string): void {
        void ajaxModal({
            modal: MODAL,
            url: `LicenseTemplate/PressForm/${id}`,
            title: null,
            // Named so the form is actually ATTACHED to a validation engine.
            // isValid() on an unattached form builds a validator with no rules
            // and answers true to everything, which is a gate that is not there.
            formId: PRESS_FORM,
            focusId: 'Name',
            errorTitle: 'Press Template',
            errorMessage: 'Failed to load Press dialog',
        });
    },

    /**
     * Issue a License from the template and put the operator in front of it.
     *
     * This is the fix the screen most needed. The legacy version opened the new
     * license's detail in #LicenseModal -- an element that exists on the License
     * list screen and NOT on the template list the operator was standing on. The
     * modal call found no container and returned silently, so pressing a
     * template showed a success toast and then nothing at all: no license, no
     * navigation, not even a refreshed list.
     *
     * Pressing is a navigation, so it navigates: getTopView rather than a bare
     * swap, which means the Licenses rail entry lights up and Back returns to
     * the templates. The new license's detail opens on arrival, where
     * #LicenseModal exists to hold it.
     */
    PressTemplateSubmit(): void {
        const form = one(`#${PRESS_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const id = (one('#LicenseTemplatePressId') as HTMLInputElement | null)?.value ?? '';
        const spin = working(PRESS_FORM, '.fa-stamp');
        spin(true);
        void ajaxJson({
            url: absoluteUrl(`LicenseTemplate/Press/${id}`),
            method: 'POST',
            data: serialize(form),
            alertInto: 'LicenseTemplatePressMessageContainer',
            errorTitle: 'Failed to press template',
            onSuccess: data => {
                const pressed = data as { id?: string; name?: string };
                hideModal(MODAL);
                toast('success', `License issued: ${pressed.name || 'new license'}`);
                void getTopView(null, 'License/Domain').then(() => {
                    if (pressed.id) LicenseList.ShowDetail(pressed.id);
                });
            },
            onFailure: failToast('Press failed'),
            onComplete: () => spin(false),
        });
    },

    Reload(): Promise<void> {
        return ajaxSwap({
            container: 'ViewContainer',
            url: `LicenseTemplate/Domain/${currentDomainId()}`,
            alertTitle: 'Failed to reload templates',
        });
    },
};

export const Detail = {
    EnterEditMode(): void {
        const modal = one('.licensetemplate-modal');
        if (!modal) return;
        modal.classList.add('editing');
        const shape = modal.querySelector('.lt-binder-shape') as HTMLTextAreaElement | null;
        if (shape) shape.readOnly = false;
        const name = modal.querySelector('.lt-name') as HTMLInputElement | null;
        name?.focus();
        name?.select();
    },

    CancelEdit(): void {
        Detail.Reload(templateId());
    },

    SaveHeader(): void {
        const id = templateId();
        const modal = one('.licensetemplate-modal');
        if (!id || !modal) return;
        const value = (selector: string): string =>
            (modal.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement | null)?.value ?? '';
        const name = value('.lt-name').trim();
        if (!name) {
            toast('danger', 'Name is required');
            return;
        }
        busy(true);
        void ajaxJson({
            url: absoluteUrl(`LicenseTemplate/${id}`),
            method: 'PUT',
            data: {
                Id: id,
                Name: name,
                Description: value('.lt-description'),
                DomainId: (one('#LicenseTemplateDomainId') as HTMLInputElement | null)?.value ?? '',
                ApplicationId: (one('#LicenseTemplateApplicationId') as HTMLInputElement | null)?.value ?? '',
                // The textarea carries a LicenseTemplateDomainBinderModel as
                // JSON; the Web controller deserializes it into the model's
                // DomainBinder property and the Api drops and recreates the
                // structured rows from it.
                domainBinderJson: value('.lt-binder-shape'),
            },
            alertInto: DETAIL_ALERT,
            errorTitle: 'Failed to save template',
            onSuccess: () => {
                toast('success', 'Template saved');
                Detail.Reload(id);
            },
            onFailure: failToast('Failed to save template'),
            onComplete: () => busy(false),
        });
    },

    Delete(): void {
        const id = templateId();
        if (!id) return;
        void confirm({
            title: 'Delete License Template',
            html: 'Delete this template?<br/><br/>Existing licenses pressed from it are not affected.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                busy(true);
                void ajaxJson({
                    url: absoluteUrl(`LicenseTemplate/${id}`),
                    method: 'DELETE',
                    alertInto: DETAIL_ALERT,
                    errorTitle: 'Failed to delete template',
                    onSuccess: () => {
                        hideModal(MODAL);
                        toast('success', 'Template deleted');
                        void List.Reload();
                    },
                    onFailure: failToast('Failed to delete template'),
                    onComplete: () => busy(false),
                });
            },
        });
    },

    AddProperty(): void {
        const id = templateId();
        const modal = one('.licensetemplate-modal');
        if (!id || !modal) return;
        const value = (suffix: string): string =>
            ((modal.querySelector(`.lt-add-prop-${suffix}`) as HTMLInputElement | null)?.value ?? '').trim();
        const claimType = value('type');
        const claimValueType = value('vtype');
        if (!claimType || !claimValueType) {
            toast('danger', 'ClaimType and ValueType are required');
            return;
        }
        void ajaxJson({
            url: absoluteUrl(`LicenseTemplate/${id}/Property`),
            method: 'POST',
            data: { ClaimType: claimType, ClaimValue: value('value'), ClaimValueType: claimValueType },
            errorTitle: 'Failed to add property',
            onSuccess: () => {
                toast('success', 'Property added');
                Detail.Reload(id);
            },
            onFailure: failToast('Failed to add property'),
        });
    },

    DeleteProperty(propertyId: number): void {
        const id = templateId();
        void confirm({
            title: 'Remove Property',
            html: 'Remove this property from the template?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`LicenseTemplate/Property/${propertyId}`),
                    method: 'DELETE',
                    errorTitle: 'Failed to delete property',
                    onSuccess: () => {
                        toast('success', 'Property removed');
                        Detail.Reload(id);
                    },
                    onFailure: failToast('Failed to delete property'),
                });
            },
        });
    },

    AddQuota(): void {
        const id = templateId();
        const modal = one('.licensetemplate-modal');
        if (!id || !modal) return;
        const field = (suffix: string): string =>
            (modal.querySelector(`.lt-add-quota-${suffix}`) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
        const counterKey = field('counter').trim();
        if (!counterKey) {
            toast('danger', 'A quota must name a counter key');
            return;
        }
        // Blank means unlimited, and the FLAG is what says so. The sentinel the
        // server stores (long.MaxValue) is past Number.MAX_SAFE_INTEGER and
        // would not survive a round trip through this form, so the number stays
        // null and the boolean carries the state -- the same pair the License
        // quota editor sends.
        const limit = field('limit');
        const soft = field('soft');
        // Period is required on a Rate row and normalised away on every other
        // measure, so it is sent only at Rate. QuotaMeasure.Rate is ordinal 2.
        const measure = parseInt(field('measure'), 10);
        const isRate = measure === 2;
        const rolling = (modal.querySelector('.lt-add-quota-rolling') as HTMLInputElement | null)?.checked === true;
        void ajaxJson({
            url: absoluteUrl(`LicenseTemplate/${id}/Quota`),
            method: 'POST',
            data: {
                CounterKey: counterKey,
                Scope: field('scope'),
                Measure: measure,
                Period: isRate ? parseInt(field('period'), 10) : null,
                Rolling: isRate && rolling,
                Limit: limit === '' ? null : limit,
                Unlimited: limit === '',
                SoftLimit: soft === '' ? null : soft,
                SoftLimitUnlimited: soft === '',
                OnExceed: field('onexceed'),
            },
            errorTitle: 'Failed to add quota',
            onSuccess: () => {
                toast('success', 'Quota added');
                Detail.Reload(id);
            },
            onFailure: failToast('Failed to add quota'),
        });
    },

    DeleteQuota(quotaId: number): void {
        const id = templateId();
        void confirm({
            title: 'Remove Quota',
            html: 'Remove this quota from the template?<br/><br/>Licenses already pressed keep theirs.',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`LicenseTemplate/Quota/${quotaId}`),
                    method: 'DELETE',
                    errorTitle: 'Failed to delete quota',
                    onSuccess: () => {
                        toast('success', 'Quota removed');
                        Detail.Reload(id);
                    },
                    onFailure: failToast('Failed to delete quota'),
                });
            },
        });
    },

    Reload(id: string): void {
        void ajaxModal({ modal: MODAL, url: `LicenseTemplate/Detail/${id}`, title: null });
    },
};

/**
 * Period and Rolling only mean anything on a Rate measure, so they are hidden
 * rather than disabled at every other one -- a control that is present but
 * meaningless reads as a setting the operator has left at a default.
 * QuotaMeasure.Rate is ordinal 2.
 *
 * An enhancer rather than a document-level listener, and one that also runs on
 * arrival: the detail modal is re-rendered on every quota write, and the
 * jQuery original only ever reacted to a change the operator made, so a
 * re-rendered Rate row showed its Period control only after being touched.
 */
export function enhanceTemplateQuotaForm(root: ParentNode): void {
    const modals: Element[] = [];
    if (root instanceof Element && root.matches('.licensetemplate-modal')) modals.push(root);
    modals.push(...Array.from(root.querySelectorAll('.licensetemplate-modal')));
    for (const modal of modals) {
        const apply = (select: HTMLSelectElement): void => {
            const cell = select.closest('td');
            if (!cell) return;
            const isRate = parseInt(select.value, 10) === 2;
            for (const node of cell.querySelectorAll('.lt-rate-only')) node.classList.toggle('d-none', !isRate);
        };
        for (const select of modal.querySelectorAll<HTMLSelectElement>('.lt-add-quota-measure')) apply(select);

        if (modal.getAttribute('data-mcp-template-bound') === 'true') continue;
        modal.setAttribute('data-mcp-template-bound', 'true');
        modal.addEventListener('change', event => {
            const target = event.target;
            if (target instanceof HTMLSelectElement && target.matches('.lt-add-quota-measure')) apply(target);
        });
    }
}
